import { useCallback, useEffect, useRef, useState } from "react";
import { MapControl, useMap } from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";

interface PolygonGeoJSON {
  type: "Polygon";
  coordinates: number[][][];
}

interface DrawingExampleProps {
  onPolygonComplete: (polygon: PolygonGeoJSON) => void;
  initialPolygon?: PolygonGeoJSON;
}

const POLYGON_STYLE: google.maps.PolygonOptions = {
  strokeColor: "#FF0000",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: "#FF0000",
  fillOpacity: 0.35,
};

export default function DrawingExample({
  onPolygonComplete,
  initialPolygon,
}: DrawingExampleProps) {
  const map = useMap();
  const [isDrawing, setIsDrawing] = useState(false);
  // Drives the Finish button's enabled state; the vertices themselves live in
  // a ref so the once-bound map listeners always see the current path.
  const [vertexCount, setVertexCount] = useState(0);
  const [hasPolygon, setHasPolygon] = useState(false);

  const finalPolygonRef = useRef<google.maps.Polygon | null>(null);
  const draftPolygonRef = useRef<google.maps.Polygon | null>(null);
  const vertsRef = useRef<google.maps.LatLngLiteral[]>([]);

  // Emit an editable polygon's current path as GeoJSON ([lng, lat] pairs).
  const emit = useCallback(
    (polygon: google.maps.Polygon) => {
      const coordinates = polygon
        .getPath()
        .getArray()
        .map((latLng) => [latLng.lng(), latLng.lat()]);
      onPolygonComplete({ type: "Polygon", coordinates: [coordinates] });
    },
    [onPolygonComplete]
  );

  // Re-emit whenever the user drags/inserts/removes a vertex of an editable
  // polygon, mirroring the old DrawingManager behavior.
  const wireEdit = useCallback(
    (polygon: google.maps.Polygon) => {
      const path = polygon.getPath();
      ["insert_at", "remove_at", "set_at"].forEach((event) =>
        google.maps.event.addListener(path, event, () => emit(polygon))
      );
    },
    [emit]
  );

  // Render the initial polygon (edit mode) and keep it as the editable target.
  useEffect(() => {
    if (
      !map ||
      !initialPolygon ||
      initialPolygon.coordinates[0]?.length === 0
    ) {
      return;
    }

    const paths = initialPolygon.coordinates[0].map((coord) => ({
      lat: coord[1],
      lng: coord[0],
    }));

    const polygon = new google.maps.Polygon({
      ...POLYGON_STYLE,
      paths,
      editable: true,
      map,
    });
    finalPolygonRef.current = polygon;
    setHasPolygon(true);

    // Fit bounds to show the polygon.
    const bounds = new google.maps.LatLngBounds();
    paths.forEach((path) => bounds.extend(path));
    map.fitBounds(bounds);

    wireEdit(polygon);

    return () => {
      polygon.setMap(null);
      finalPolygonRef.current = null;
      setHasPolygon(false);
    };
  }, [map, initialPolygon, wireEdit]);

  const finishDrawing = useCallback(() => {
    const verts = vertsRef.current;
    draftPolygonRef.current?.setMap(null);
    draftPolygonRef.current = null;
    vertsRef.current = [];
    setVertexCount(0);
    setIsDrawing(false);

    // A polygon needs at least 3 points — otherwise treat finish as a cancel.
    if (verts.length < 3 || !map) return;

    // Replace any previously drawn polygon.
    finalPolygonRef.current?.setMap(null);

    const polygon = new google.maps.Polygon({
      ...POLYGON_STYLE,
      paths: verts,
      editable: true,
      map,
    });
    finalPolygonRef.current = polygon;
    setHasPolygon(true);
    emit(polygon);
    wireEdit(polygon);
  }, [map, emit, wireEdit]);

  const cancelDrawing = useCallback(() => {
    draftPolygonRef.current?.setMap(null);
    draftPolygonRef.current = null;
    vertsRef.current = [];
    setVertexCount(0);
    setIsDrawing(false);
  }, []);

  // Drawing interaction: click adds a vertex, double-click finishes.
  useEffect(() => {
    if (!map || !isDrawing) return;

    const prevCursor = map.get("draggableCursor") as string | null | undefined;
    map.setOptions({
      draggableCursor: "crosshair",
      disableDoubleClickZoom: true,
    });

    const clickListener = map.addListener(
      "click",
      (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        vertsRef.current = [...vertsRef.current, e.latLng.toJSON()];
        setVertexCount(vertsRef.current.length);

        if (!draftPolygonRef.current) {
          draftPolygonRef.current = new google.maps.Polygon({
            ...POLYGON_STYLE,
            map,
            editable: false,
            clickable: false,
          });
        }
        draftPolygonRef.current.setPath(vertsRef.current);
      }
    );

    const dblClickListener = map.addListener("dblclick", () => {
      // A double-click fires two `click`s first, adding a duplicate vertex at
      // the final point — drop it before closing the polygon.
      vertsRef.current = vertsRef.current.slice(0, -1);
      finishDrawing();
    });

    return () => {
      clickListener.remove();
      dblClickListener.remove();
      map.setOptions({
        draggableCursor: prevCursor ?? null,
        disableDoubleClickZoom: false,
      });
    };
  }, [map, isDrawing, finishDrawing]);

  const startDrawing = useCallback(() => {
    vertsRef.current = [];
    setVertexCount(0);
    setIsDrawing(true);
  }, []);

  return (
    <MapControl position={google.maps.ControlPosition.TOP_CENTER}>
      <div className="m-2 flex items-center gap-2 rounded-md bg-white px-2 py-1 shadow-md">
        {isDrawing ? (
          <>
            <span className="text-xs text-muted-foreground">
              Click to add points, double-click to finish
            </span>
            <Button
              type="button"
              size="sm"
              onClick={finishDrawing}
              disabled={vertexCount < 3}
            >
              Finish
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={cancelDrawing}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button type="button" size="sm" onClick={startDrawing}>
            {hasPolygon ? "Redraw area" : "Draw area"}
          </Button>
        )}
      </div>
    </MapControl>
  );
}
